import { z } from 'zod';
import { type CatalogEntry, defineCatalogEntry, SymbolSchema } from './entry';

/**
 * Market panels of the board catalog — owned by the Market area. Add market entries HERE,
 * never in another area's file; a duplicate id across area files is a compile error
 * (index.ts).
 *
 * Every entry must fetch its own data from simple PROPS (ids, enums) through its feature's
 * api-query hooks — a component that takes DATA props fails that criterion.
 *
 * The enums below MIRROR lists the web app owns (the ownership views, the calendar kinds, the
 * screener sort keys). They are copied because shared-types cannot import the app; a drift is
 * caught by `apps/web/src/features/chatbot/boards/market-catalog-parity.spec.ts`, which fails
 * the day the app's list and this one disagree.
 *
 * ZOD-3 COMPATIBILITY: apps/web bundles this SOURCE with zod v3 while the backend runs v4 —
 * only syntax valid in BOTH may be used here.
 *
 * Lifecycle rules (§4.6) — an id is NEVER repurposed (persisted ops reference it forever);
 * a semantic change is a NEW entry; `retired: true` drops an entry from the composer
 * prompt while its FE binding stays so historical boards keep rendering. Props changes are
 * additive (new fields optional/defaulted); a breaking change = new id + retire the old.
 */

/** `OWNERSHIP_VIEWS` in apps/web `features/research/logic/ownership-page.ts`. */
const OWNERSHIP_VIEW_IDS = [
  'summary',
  'executives',
  'institutions',
  'funds',
  'insiders',
  'congress',
] as const;

/** `CALENDAR_KINDS` in apps/web `features/research/logic/calendar-view-state.ts`. */
const CALENDAR_KIND_IDS = [
  'economic',
  'central-bank',
  'earnings',
  'earnings-call',
  'dividend',
  'ipo',
  'holiday',
] as const;

/** `SCREENER_SORT_KEYS` in apps/web `features/research/logic/screener-filters.ts`. */
const SCREENER_SORT_KEY_IDS = [
  'symbol',
  'points',
  'overview',
  'dividends',
  'growth',
  'solidity',
  'momentum',
  'percentile',
  'fvDiscount',
  'marketCap',
  'pe',
  'dividendYield',
] as const;

export const MARKET_PANELS = {
  ownership: defineCatalogEntry({
    id: 'ownership',
    category: 'data',
    description:
      'Who owns and who trades one US company: institutional/fund holders, management stakes, insider (Form 4) and congressional trades. Use for "who owns X", "are insiders buying or selling X".',
    props: z.object({
      symbol: SymbolSchema,
      view: z.enum(OWNERSHIP_VIEW_IDS).default('summary'),
    }),
    adjustable: ['view'],
    breadcrumb: ['Research', 'Ownership'],
    example: { symbol: 'AAPL.US', view: 'insiders' },
  }),
  'peer-compare': defineCatalogEntry({
    id: 'peer-compare',
    category: 'valuation',
    description:
      'Side-by-side table of one company against its peer group: growth, margins, ROE, P/E, Rule of 40 and peer percentiles. With no peers (the default) it discovers the group itself; pass peers to pin the comparison to named companies. Use for "how does X stack up against its competitors" (stocks only, not ETFs).',
    props: z.object({
      symbol: SymbolSchema,
      peers: z.array(SymbolSchema).max(5).default([]),
    }),
    adjustable: ['peers'],
    breadcrumb: ['Research', 'Compare'],
    example: { symbol: 'MSFT.US', peers: ['GOOGL.US', 'AMZN.US'] },
  }),
  'events-calendar': defineCatalogEntry({
    id: 'events-calendar',
    category: 'market',
    description:
      'Month calendar of dated events around one company: its earnings dates and calls, dividend ex-dates, plus the macro releases, central-bank meetings, IPOs and market holidays of its market. Use for "when does X report", "what is coming up for X this month".',
    props: z.object({
      symbol: SymbolSchema,
      kinds: z
        .array(z.enum(CALENDAR_KIND_IDS))
        .min(1)
        .default([...CALENDAR_KIND_IDS]),
    }),
    adjustable: ['kinds'],
    breadcrumb: ['Research', 'Calendar'],
    example: { symbol: 'NVDA.US', kinds: ['earnings', 'earnings-call', 'dividend'] },
  }),
  screener: defineCatalogEntry({
    id: 'screener',
    category: 'market',
    description:
      'Ranked list of the whole scored stock universe (Stoxly points, category scores, fair-value discount, P/E, yield), ordered by one column. Not about one company. Use for "which stocks score best on dividends", "most undervalued stocks", "cheapest by P/E".',
    props: z.object({
      sortKey: z.enum(SCREENER_SORT_KEY_IDS).default('points'),
      sortDir: z.enum(['asc', 'desc']).default('desc'),
    }),
    adjustable: ['sortKey', 'sortDir'],
    breadcrumb: ['Research', 'Screener'],
    example: { sortKey: 'fvDiscount', sortDir: 'desc' },
  }),
  'market-news': defineCatalogEntry({
    id: 'market-news',
    category: 'news',
    description:
      'The market pulse: today\'s breaking market roll-ups and the daily AI market-context summaries of recent days, market-wide rather than per symbol. Use for "what is moving the market today", "why is the market down".',
    props: z.object({}),
    adjustable: [],
    breadcrumb: ['News', 'Market pulse'],
    example: {},
  }),
} as const satisfies Record<string, CatalogEntry>;
